import {FC, memo, useEffect, useRef, useState} from 'react'
import {useQuery, useQueryClient} from 'react-query'
import {toAbsoluteUrl} from '../../../_metronic/helpers'
import styles from './kiosk.module.css'
import {
  getKioskPlants,
  getKioskLogo,
  getKioskSummary,
  isKioskUnauthorized,
  KioskPlant,
  KioskPlantsPage,
} from './kioskApi'

const PAGE_INTERVAL_MS = 15000
const PAGE_SIZE = 15
// Resumo (métricas do topo) é o "pulso" da TV: detecta alterações e desconexão pela empresa
const SUMMARY_POLL_MS = 5000
const PLANTS_STALE_MS = 10000
const PLANTS_REFETCH_MS = 30000

const STATUS_COLORS: Record<string, string> = {
  success: 'var(--k-success)',
  danger: 'var(--k-danger)',
  warning: 'var(--k-warning)',
  info: 'var(--k-info)',
  secondary: 'var(--k-secondary)',
}

// Mesmo mapeamento de status da listagem do dashboard (TableV1)
const statusColor = (status: number | null) => {
  if (status === 0) return STATUS_COLORS.success
  if (status === 1) return STATUS_COLORS.danger
  if (status === 2) return STATUS_COLORS.warning
  if (status === 3 || status === 7) return STATUS_COLORS.info
  return STATUS_COLORS.secondary
}

const numberFormat = new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 1})

const formatNumber = (value: number | string | null | undefined) => {
  const num = Number(value)
  return value === null || value === undefined || value === '' || Number.isNaN(num) ? '-' : numberFormat.format(num)
}

const Perf: FC<{value: number}> = ({value}) => {
  if (!value) return <span className={`${styles.perf} ${styles.perfNone}`}>-</span>
  return (
    <span className={`${styles.perf} ${value >= 100 ? styles.perfGood : styles.perfBad}`}>
      {Math.round(value)}%
    </span>
  )
}

const PlantRow = memo(({plant}: {plant: KioskPlant}) => (
  <tr className={styles.row}>
    <td>
      <div className={styles.nameCell}>
        <span className={styles.statusBar} style={{background: statusColor(plant.status)}} />
        <span className={styles.name}>{plant.name}</span>
      </div>
    </td>
    <td>
      <div className={styles.markCell}>
        {plant.mark?.link_imagem && (
          <img
            className={styles.markLogo}
            src={/^https?:\/\//.test(plant.mark.link_imagem) ? plant.mark.link_imagem : toAbsoluteUrl(plant.mark.link_imagem)}
            alt=''
            loading='lazy'
            decoding='async'
          />
        )}
        <span>{plant.mark?.name || (plant.type_plant === 'COMPOSTA' ? 'Composta' : '-')}</span>
      </div>
    </td>
    <td>
      <span className={styles.num}>{formatNumber(plant.capacity)}</span>
      <span className={styles.unit}>kWp</span>
    </td>
    <td>
      <span className={styles.num}>{formatNumber(plant.pac)}</span>
      <span className={styles.unit}>kW</span>
    </td>
    <td>
      <span className={styles.num}>{formatNumber(plant.energyToday)}</span>
      <span className={styles.unit}>kWh</span>
    </td>
    <td>
      <span className={styles.num}>{formatNumber(plant.energyMonth)}</span>
      <span className={styles.unit}>kWh</span>
    </td>
    <td>
      <div className={styles.perfGroup}>
        <Perf value={plant.percentage_day} />
        <Perf value={plant.percentage_15days} />
        <Perf value={plant.percentage_30days} />
        <Perf value={plant.percentage_12mouth} />
      </div>
    </td>
    <td>
      {plant.alarmsCount && plant.alarmsCount > 0 ? (
        <span className={styles.alarm}>
          <i className='bi bi-exclamation-triangle-fill' style={{color: 'inherit'}}></i> {plant.alarmsCount}
        </span>
      ) : (
        <span className={styles.unit}>-</span>
      )}
    </td>
  </tr>
))

const EmptyRow = memo(() => (
  <tr className={`${styles.row} ${styles.emptyRow}`}>
    <td colSpan={8} />
  </tr>
))

// Relógio isolado: re-renderiza só ele a cada 30s, não o painel inteiro
const Clock: FC = () => {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000)
    return () => clearInterval(timer)
  }, [])

  return (
    <div className={styles.clock}>
      <div className={styles.clockTime}>
        {now.toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}
      </div>
      <div className={styles.clockDate}>
        {now.toLocaleDateString('pt-BR', {weekday: 'long', day: '2-digit', month: 'long'})}
      </div>
    </div>
  )
}

const useOnline = () => {
  const [online, setOnline] = useState(() => navigator.onLine)

  useEffect(() => {
    const up = () => setOnline(true)
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [])

  return online
}

type Props = {
  onUnauthorized: () => void
}

const KioskBoard: FC<Props> = ({onUnauthorized}) => {
  const queryClient = useQueryClient()
  const online = useOnline()
  const [page, setPage] = useState(1)
  const lastPageRef = useRef(1)

  const queryOptions = {
    refetchOnWindowFocus: false,
    // Sem isso o react-query pausa o polling quando a aba não está visível
    refetchIntervalInBackground: true,
    retry: (failureCount: number, error: any) => !isKioskUnauthorized(error) && failureCount < 1,
    onError: (error: any) => {
      if (isKioskUnauthorized(error)) onUnauthorized()
    },
  }

  const plantsQuery = useQuery<KioskPlantsPage>(
    ['kiosk-plants', page],
    () => getKioskPlants(page),
    // refetchInterval cobre o caso de página única, em que a chave nunca muda
    {...queryOptions, keepPreviousData: true, staleTime: PLANTS_STALE_MS, refetchInterval: PLANTS_REFETCH_MS}
  )

  // Logo muda raramente: busca uma vez (a página inteira recarrega a cada 6h)
  const logoQuery = useQuery(['kiosk-logo'], getKioskLogo, {
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    retry: 1,
  })

  const summaryQuery = useQuery(['kiosk-summary'], getKioskSummary, {
    ...queryOptions,
    refetchInterval: SUMMARY_POLL_MS,
  })

  // Alguma usina mudou (status, geração, nova importação...): recarrega a página exibida agora
  // e marca as demais como desatualizadas para buscarem de novo ao entrar na tela
  const version = summaryQuery.data?.version
  const versionRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!version) return
    if (versionRef.current && versionRef.current !== version) {
      queryClient.invalidateQueries('kiosk-plants')
    }
    versionRef.current = version
  }, [version, queryClient])

  const lastPage = plantsQuery.data?.lastPage || 1
  lastPageRef.current = lastPage

  // Troca de página fixa a cada 15s, voltando para a primeira após a última
  useEffect(() => {
    const timer = setInterval(() => {
      setPage((current) => (current >= lastPageRef.current ? 1 : current + 1))
    }, PAGE_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [])

  // Se o total de usinas diminuir, não fica preso numa página que não existe mais
  useEffect(() => {
    if (page > lastPage) setPage(1)
  }, [page, lastPage])

  // Pré-carrega a próxima página para a troca ser instantânea
  useEffect(() => {
    const next = page >= lastPage ? 1 : page + 1
    if (next === page) return
    queryClient.prefetchQuery(['kiosk-plants', next], () => getKioskPlants(next), {
      staleTime: PLANTS_STALE_MS,
    })
  }, [page, lastPage, queryClient])

  const plants = plantsQuery.data?.data || []
  const summary = summaryQuery.data
  const shownPage = plantsQuery.data?.page || page
  const offline = !online || plantsQuery.isError

  if (plantsQuery.isLoading && !plantsQuery.data) {
    return <div className={styles.center}>Carregando usinas...</div>
  }

  return (
    <>
      <div className={styles.header}>
        {logoQuery.data?.logo && (
          <div className={styles.brandLogo}>
            <img src={logoQuery.data.logo} alt='' />
          </div>
        )}
        <div className={styles.brand}>
          <div className={styles.brandName}>{summary?.companyName || 'Monitoramento de Usinas'}</div>
          <div className={styles.brandSub}>{summary?.deviceName || 'Modo TV'}</div>
        </div>
        <div className={styles.counters}>
          <div className={styles.counter}>Total <b>{summary?.total ?? '-'}</b></div>
          <div className={styles.counter} style={{color: STATUS_COLORS.success}}>Normais <b>{summary?.normalNum ?? '-'}</b></div>
          <div className={styles.counter} style={{color: STATUS_COLORS.warning}}>Em alerta <b>{summary?.attention ?? '-'}</b></div>
          <div className={styles.counter} style={{color: STATUS_COLORS.danger}}>Offline <b>{summary?.offlineNum ?? '-'}</b></div>
          <div className={styles.counter} style={{color: STATUS_COLORS.info}}>Informativo <b>{summary?.info ?? '-'}</b></div>
        </div>
        <Clock />
      </div>

      <div className={styles.progressTrack}>
        <div
          key={`${page}-${lastPage}`}
          className={styles.progressBar}
          style={{animationDuration: `${PAGE_INTERVAL_MS}ms`}}
        />
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <colgroup>
            <col style={{width: '26%'}} />
            <col style={{width: '13%'}} />
            <col style={{width: '8%'}} />
            <col style={{width: '8%'}} />
            <col style={{width: '9%'}} />
            <col style={{width: '10%'}} />
            <col style={{width: '20%'}} />
            <col style={{width: '6%'}} />
          </colgroup>
          <thead>
            <tr>
              <th>Usina</th>
              <th>Marca</th>
              <th>Potência</th>
              <th>Atual</th>
              <th>Hoje</th>
              <th>Mês</th>
              <th>Desempenho (1D · 15D · 30D · 12M)</th>
              <th>Alarmes</th>
            </tr>
          </thead>
          <tbody key={shownPage}>
            {plants.map((plant) => <PlantRow key={plant.id} plant={plant} />)}
            {Array.from({length: Math.max(0, PAGE_SIZE - plants.length)}).map((_, index) => (
              <EmptyRow key={`empty-${index}`} />
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.footer}>
        <span>
          {offline
            ? <span className={styles.offlineBadge}>● Sem conexão — exibindo últimos dados</span>
            : `${plantsQuery.data?.total ?? 0} usinas`}
        </span>
        <span>Página {shownPage} de {lastPage}</span>
      </div>
    </>
  )
}

export {KioskBoard}
